// @flow
import moment from 'moment';
import DateRange from 'common/domain/DateRange';

export type DateRangePeriod = 'FULL_WEEK' | 'WEEK_DAYS' | 'WEEKEND' | 'SINGLE_DAY';

export const yesterday = () => {
  return moment().subtract(1, 'day');
};

export const getLastSevenDays = () => {
  return new DateRange({ from: moment().subtract(7, 'days').startOf('day'), to: moment().subtract(1, 'days').endOf('day') });
};

export const getPreviousPeriod = (period: DateRangePeriod, maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  if (period === 'FULL_WEEK') {
    return getPreviousFullWeekPeriod(maxDate, minDate, dateRange);
  }

  if (period === 'WEEK_DAYS') {
    return getPreviousWeekDaysPeriod(maxDate, minDate, dateRange);
  }

  if (period === 'WEEKEND') {
    return getPreviousWeekendPeriod(maxDate, minDate, dateRange);
  }

  return getPreviousSingleDayPeriod(maxDate, minDate, dateRange);
};

export const getPeriod = (period: DateRangePeriod, maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  if (dateRange && minDate) {
    if (period === 'FULL_WEEK') {
      return getFullWeekPeriod(dateRange, maxDate, minDate);
    }

    if (period === 'WEEK_DAYS') {
      return getWeekDaysPeriod(dateRange, maxDate, minDate);
    }

    if (period === 'WEEKEND') {
      return getWeekendPeriod(dateRange, maxDate, minDate);
    }

    return getSingleDayPeriod(dateRange);
  }

  return getPreviousPeriod(period, maxDate);
};

export const getPreviousFullWeekPeriod = (maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  const lastMonday = -6;
  const startOfLastWeek = getStartOfPreviousPeriod(lastMonday, maxDate, minDate, dateRange);
  const endOfLastWeek = startOfLastWeek.clone().add(6, 'days').endOf('day');

  return new DateRange({ from: startOfLastWeek, to: endOfLastWeek });
};

const getPreviousWeekendPeriod = (maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  const lastSaturday = -1;
  const startOfLastWeekEnd = getStartOfPreviousPeriod(lastSaturday, maxDate, minDate, dateRange);
  const endOfLastWeekEnd = startOfLastWeekEnd.clone().add(1, 'days').endOf('day');

  return new DateRange({ from: startOfLastWeekEnd, to: endOfLastWeekEnd });
};

const getPreviousWeekDaysPeriod = (maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  const lastMonday = -6;
  const startOfLastWeekDays = getStartOfPreviousPeriod(lastMonday, maxDate, minDate, dateRange);
  const endOfLastWeekDays = startOfLastWeekDays.clone().add(4, 'days').endOf('day');

  return new DateRange({ from: startOfLastWeekDays, to: endOfLastWeekDays });
};

const getPreviousSingleDayPeriod = (maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  if (dateRange && minDate) {
    const oneWeekBefore = dateRange.from.clone().subtract(7, 'days').startOf('day');
    if (oneWeekBefore.isBefore(minDate)) {
      return new DateRange({ from: dateRange.from, to: dateRange.to });
    }

    return new DateRange({ from: oneWeekBefore, to: oneWeekBefore });
  }

  return new DateRange({ from: maxDate, to: maxDate });
};

const getStartOfPreviousPeriod = (weekDay: number, maxDate: moment, minDate?: moment, dateRange?: DateRange) => {
  if (dateRange && minDate) {
    const startOfPeriod = dateRange.from.clone().isoWeekday(weekDay).startOf('day');

    if (startOfPeriod.isBefore(minDate)) {
      const weekDayOneWeekLater = weekDay + 7;
      return dateRange.from.clone().isoWeekday(weekDayOneWeekLater).startOf('day');
    }

    return startOfPeriod;
  }

  return maxDate.clone().isoWeekday(weekDay).startOf('day');
};

const getFullWeekPeriod = (dateRange: DateRange, maxDate: moment, minDate: moment) => {
  const sunday = dateRange.from.clone().isoWeekday(7).endOf('day');

  if (sunday.isBefore(maxDate)) {
    const monday = dateRange.from.clone().isoWeekday(1).startOf('day');

    if (monday.isBefore(minDate)) {
      const oneWeek = 7;
      return new DateRange({ from: monday.add(oneWeek, 'days'), to: sunday.add(oneWeek, 'days') });
    }
    return new DateRange({ from: monday, to: sunday });
  }

  return getPreviousFullWeekPeriod(maxDate, minDate, dateRange);
};

const getWeekDaysPeriod = (dateRange: DateRange, maxDate: moment, minDate: moment) => {
  const friday = dateRange.from.clone().isoWeekday(5).endOf('day');

  if (friday.isBefore(maxDate)) {
    const monday = dateRange.from.clone().isoWeekday(1).startOf('day');

    if (monday.isBefore(minDate)) {
      const oneWeek = 7;
      return new DateRange({ from: monday.add(oneWeek, 'days'), to: friday.add(oneWeek, 'days') });
    }
    return new DateRange({ from: monday, to: friday });
  }

  return getPreviousWeekDaysPeriod(maxDate, minDate, dateRange);
};

const getWeekendPeriod = (dateRange: DateRange, maxDate: moment, minDate: moment) => {
  const sunday = dateRange.from.clone().isoWeekday(7).endOf('day');

  if (sunday.isBefore(maxDate)) {
    const saturday = dateRange.from.clone().isoWeekday(6).startOf('day');

    if (saturday.isBefore(minDate)) {
      const oneWeek = 7;
      return new DateRange({ from: saturday.add(oneWeek, 'days'), to: sunday.add(oneWeek, 'days') });
    }
    return new DateRange({ from: saturday, to: sunday });
  }

  return getPreviousWeekendPeriod(maxDate, minDate, dateRange);
};

const getSingleDayPeriod = (dateRange: DateRange) => {
  return new DateRange({ from: dateRange.from, to: dateRange.from });
};



// WEBPACK FOOTER //
// ./src/statsradio/modules/metrics/domain/MetricsPeriods.js